'use client';

import { type MqttConfigItem, MqttConfigsPanel } from '@slims/ui/organisms';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { env } from '@/env.generated';
import { api } from '@/lib/utils/api';

/** Il feed arriva ~1 messaggio/min dal job n8n → poll allineato. */
const POLL_MS = 60_000;

interface OverviewResponse {
	configs: {
		id: string;
		name: string;
		enabled: boolean;
		lastReceivedAt: string | null;
		pods: { pod: string; lastReadAt: string; obis: string; value: number; matched: boolean }[];
	}[];
	metersWithoutData: { pod: string; kind: string }[];
}

interface MqttLiveSectionProps {
	/** Configurazione CER (org) di cui gestire l'ingest MQTT */
	orgId: string;
}

/**
 * Wiring del pannello ingest MQTT nella pagina Dettagli configurazione:
 * gestisce gli URL di ingest (uno per job n8n) e il confronto tra i POD
 * inoltrati dal broker e quelli censiti nella configurazione.
 */
export function MqttLiveSection({ orgId }: MqttLiveSectionProps) {
	const [configs, setConfigs] = useState<MqttConfigItem[]>([]);
	const [metersWithoutData, setMetersWithoutData] = useState<{ pod: string; kind: string }[]>([]);
	const [loading, setLoading] = useState(false);

	const fetchOverview = useCallback(async () => {
		setLoading(true);
		try {
			const res = await api.get<OverviewResponse>('/api/live-data/overview', { orgId });
			setConfigs(
				res.configs.map((config) => ({
					id: config.id,
					name: config.name,
					url: `${env.API_BASE_URL}/api/gateway/mqtt/${config.id}`,
					lastReceivedAt: config.lastReceivedAt,
					pods: config.pods,
				})),
			);
			setMetersWithoutData(res.metersWithoutData);
		} catch {
			// pannello secondario: mantieni l'ultimo stato noto
		} finally {
			setLoading(false);
		}
	}, [orgId]);

	useEffect(() => {
		fetchOverview();
		const timer = setInterval(fetchOverview, POLL_MS);
		return () => clearInterval(timer);
	}, [fetchOverview]);

	const handleCreate = useCallback(
		async (name: string) => {
			try {
				await api.post(`/api/live-data/configs?orgId=${orgId}`, name ? { name } : {});
				await fetchOverview();
				toast.success('Configurazione MQTT creata - incolla l’URL nel job n8n');
			} catch (e) {
				toast.error(e instanceof Error ? e.message : 'Creazione fallita');
			}
		},
		[orgId, fetchOverview],
	);

	const handleDelete = useCallback(
		async (configId: string) => {
			try {
				await api.delete(`/api/live-data/configs/${configId}?orgId=${orgId}`);
				await fetchOverview();
				toast.success('Configurazione eliminata - URL di ingest revocato');
			} catch (e) {
				toast.error(e instanceof Error ? e.message : 'Eliminazione fallita');
			}
		},
		[orgId, fetchOverview],
	);

	return (
		<MqttConfigsPanel
			configs={configs}
			metersWithoutData={metersWithoutData}
			loading={loading}
			onCreate={handleCreate}
			onDelete={handleDelete}
			onRefresh={fetchOverview}
		/>
	);
}
